import { rollRandomCountry, WORLD_COUNTRIES } from '../data/worldDemographics.ts';
import { LIFE_EVENTS_POOL, LIFE_STAGES } from '../data/oneLifeEvents.ts';
import type {
  CharacterAttributes,
  DemographicCountry,
  FaithLifeInfo,
  Gender,
  InitialBackground,
  LifeChoice,
  LifeDecisionRecord,
  LifeEvent,
  LifeStageKey,
  LifeSummary,
} from '../types/onelife.ts';

export const CATHOLIC_SAINTS_MALE = [
  { name: 'Giuse', patron: 'Thánh Cả Giuse (Quan Thầy Giới Lao Động & Các Gia Đình)' },
  { name: 'Phêrô', patron: 'Thánh Phêrô Tông Đồ (Tảng Đá Đức Tin & Đấng Chăn Dắt)' },
  { name: 'Phaolô', patron: 'Thánh Phaolô Tông Đồ (Vị Thầy Dân Ngoại & Nhiệt Thành Tin Mừng)' },
  { name: 'Gioan', patron: 'Thánh Gioan Tông Đồ (Môn Đệ Chúa Yêu & Chứng Nhân Tình Yêu)' },
  { name: 'Phanxicô Xaviê', patron: 'Thánh Phanxicô Xaviê (Bổn Mạng Các Xứ Truyền Giáo)' },
  { name: 'Antôn', patron: 'Thánh Antôn Pa-đô-va (Tiến Sĩ Tin Mừng & Hay Làm Phép Lạ)' },
  { name: 'Micae', patron: 'Tổng Lãnh Thiên Thần Micae (Đấng Chiến Thắng Bóng Tối)' },
  { name: 'Đaminh', patron: 'Thánh Đaminh (Đấng Sáng Lập Dòng Giảng Thuyết & Đức Khôn Ngoan)' },
];

export const CATHOLIC_SAINTS_FEMALE = [
  { name: 'Maria', patron: 'Đức Maria Mẹ Thiên Chúa (Nữ Vương Ban Sự Bình An)' },
  { name: 'Têrêsa', patron: 'Thánh Têrêsa Hài Đồng Giêsu (Con Đường Thơ Ấu Thiêng Liêng)' },
  { name: 'Anna', patron: 'Thánh Anna (Thân Mẫu Đức Trinh Nữ Maria)' },
  { name: 'Cêcilia', patron: 'Thánh Cêcilia (Quan Thầy Nghệ Thuật & Thánh Nhạc)' },
  { name: 'Mácta', patron: 'Thánh Mácta (Bổn Mạng Tinh Thần Đón Tiếp & Phục Vụ)' },
  { name: 'Monica', patron: 'Thánh Monica (Tấm Gương Cầu Nguyện Kiên Trì Của Người Mẹ)' },
  { name: 'Inê', patron: 'Thánh Inê (Trinh Nữ Tử Đạo Anh Dũng & Trong Trắng)' },
  { name: 'Rosa', patron: 'Thánh Rosa Lima (Bông Hoa Khiêm Nhường Dâng Hiến)' },
];

export function computeFaithLife(
  gender: Gender,
  age: number,
  relationship: string,
  saintName: string,
  patronSaintVi: string
): FaithLifeInfo {
  // TUÂN THỦ NGHIÊM NGẶT QUY TẮC: Chữ "Bí Tích" BẮT BUỘC PHẢI VIẾT HOA CẢ HAI TỪ
  const sacraments: string[] = ['Bí Tích Rửa Tội'];
  if (age >= 7) sacraments.push('Bí Tích Thánh Thể');
  if (age >= 12) sacraments.push('Bí Tích Thêm Sức');
  if (relationship.includes('kết hôn') || relationship.includes('gia đình') || relationship.includes('con') || age >= 25) {
    sacraments.push('Bí Tích Hôn Phối');
  }
  sacraments.push('Bí Tích Xức Dầu Bệnh Nhân');

  const virtuesPool = [
    'Siêng năng kinh nguyện gia đình và tham dự Thánh Lễ Chúa Nhật',
    'Thực thi đức bác ái, rộng lòng nâng đỡ người nghèo khó neo đơn',
    'Trung kiên giữ vững đức tin qua bao thăng trầm của phận người',
    'Sống chan hòa, khiêm nhường, đem lại bình an cho xóm giềng',
    'Dạy dỗ con cháu sống hiếu thảo, kính sợ Chúa và yêu thương tha nhân',
    'Trọn niềm phó thác tâm hồn trong ân sủng và lòng thương xót của Chúa',
  ];

  return {
    saintName,
    patronSaintVi,
    sacramentsReceived: sacraments,
    charityAndVirtues: [
      virtuesPool[Math.floor(Math.random() * virtuesPool.length)],
      'Một đời bình an, trung thành gìn giữ gia sản đức tin',
    ],
  };
}

export interface OneLifeState {
  id: string;
  characterName: string;
  saintName?: string;
  gender: Gender;
  birthYear: number;
  currentAge: number;
  currentStageIndex: number;
  country: DemographicCountry;
  regionNameVi: string;
  familyClassVi: string;
  educationVi?: string;
  causeOfDeathVi?: string;
  faithLife?: FaithLifeInfo;
  attributes: CharacterAttributes;
  isAlive: boolean;
  career: string;
  relationshipStatus: string;
  childrenCount: number;
  placesLived: string[];
  achievements: string[];
  decisionHistory: LifeDecisionRecord[];
  isFastMode: boolean;
  completedAt?: number;
}

export function generateNewDestiny(options?: {
  forcedCountryCode?: string;
  characterName?: string;
  gender?: Gender;
  isFastMode?: boolean;
  drawMode?: 'annual_births' | 'population';
}): OneLifeState {
  const drawMode = options?.drawMode || 'annual_births';
  const country = options?.forcedCountryCode
    ? WORLD_COUNTRIES.find((c) => c.code === options.forcedCountryCode) || rollRandomCountry(drawMode)
    : rollRandomCountry(drawMode);

  const gender: Gender = options?.gender || (Math.random() < 0.5 ? 'male' : 'female');
  const birthYear = 1995;

  // Determine urban vs rural
  const isUrban = Math.random() * 100 < country.urbanPercentage;
  const isCapital = isUrban && Math.random() < 0.35;
  const regionNameVi = isCapital
    ? `Thủ đô & Đại đô thị (${country.nameVi})`
    : isUrban
    ? `Thành phố công nghiệp (${country.nameVi})`
    : `Làng quê nông nghiệp (${country.nameVi})`;

  // Determine family class based on HDI tier
  let familyClassVi = 'Công Nhân / Lao Động Phổ Thông';
  let initialWealth = 35;
  const classRoll = Math.random();

  if (country.hdiTier === 'very_high') {
    if (classRoll < 0.1) {
      familyClassVi = 'Gia Đình Khó Khăn';
      initialWealth = 20;
    } else if (classRoll < 0.65) {
      familyClassVi = 'Tầng Lớp Trung Lưu Đô Thị';
      initialWealth = 60;
    } else {
      familyClassVi = 'Gia Đình Khá Giả / Trí Thức';
      initialWealth = 80;
    }
  } else if (country.hdiTier === 'low') {
    if (classRoll < 0.6) {
      familyClassVi = 'Hộ Nghèo / Thuần Nông';
      initialWealth = 15;
    } else if (classRoll < 0.9) {
      familyClassVi = 'Gia Đình Lao Động Bình Dân';
      initialWealth = 30;
    } else {
      familyClassVi = 'Thương Nhân Địa Phương';
      initialWealth = 55;
    }
  } else {
    // medium & high
    if (classRoll < 0.3) {
      familyClassVi = 'Gia Đình Nông Thôn / Khó Khăn';
      initialWealth = 25;
    } else if (classRoll < 0.8) {
      familyClassVi = 'Tầng Lớp Lao Động Vừa Đủ Ăn';
      initialWealth = 45;
    } else {
      familyClassVi = 'Công Chức / Hộ Kinh Doanh';
      initialWealth = 70;
    }
  }

  const initialHealth = Math.min(95, Math.max(45, Math.round(55 + (country.lifeExpectancyAverage - 60) * 1.2 + (initialWealth - 40) * 0.2)));
  const initialHappiness = Math.round(60 + Math.random() * 25);
  const initialIntellect = Math.round(45 + Math.random() * 25);
  const initialRelations = Math.round(65 + Math.random() * 20);

  const defaultName = gender === 'male'
    ? (country.code === 'VN' ? 'Nguyễn Văn Minh' : `Alexander ${country.code}`)
    : (country.code === 'VN' ? 'Nguyễn Thị Mai' : `Elena ${country.code}`);

  const saintsPool = gender === 'male' ? CATHOLIC_SAINTS_MALE : CATHOLIC_SAINTS_FEMALE;
  const pickedSaint = saintsPool[Math.floor(Math.random() * saintsPool.length)];
  const saintName = pickedSaint.name;

  return {
    id: `life_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    characterName: options?.characterName || defaultName,
    saintName,
    gender,
    birthYear,
    currentAge: 0,
    currentStageIndex: 0,
    country,
    regionNameVi,
    familyClassVi,
    educationVi: 'Giáo dục Khởi đầu',
    faithLife: computeFaithLife(gender, 0, 'Độc Thân', saintName, pickedSaint.patron),
    attributes: {
      health: initialHealth,
      happiness: initialHappiness,
      wealth: initialWealth,
      intellect: initialIntellect,
      relations: initialRelations,
    },
    isAlive: true,
    career: 'Trẻ Thơ',
    relationshipStatus: 'Độc Thân',
    childrenCount: 0,
    placesLived: [regionNameVi],
    achievements: ['Chào Đời Bình An'],
    decisionHistory: [],
    isFastMode: options?.isFastMode ?? true,
  };
}

export function getCurrentStageEvents(state: OneLifeState): LifeEvent[] {
  const currentStage = LIFE_STAGES[state.currentStageIndex];
  if (!currentStage) return [];

  const answeredTitles = new Set(state.decisionHistory.map((d) => d.eventTitle));

  // Eligible pool
  let matched = LIFE_EVENTS_POOL.filter((e) => {
    if (e.stageKey !== currentStage.key) return false;
    if (answeredTitles.has(e.titleVi)) return false;

    // Gender check
    if (e.requiredGender && e.requiredGender !== state.gender) return false;

    // Religion check
    if (e.requiredReligion === 'catholic' && state.faithLife?.saintName === undefined) return false;

    // Wealth check
    if (e.minWealth !== undefined && state.attributes.wealth < e.minWealth) return false;
    if (e.maxWealth !== undefined && state.attributes.wealth > e.maxWealth) return false;

    return true;
  });

  // Fallback if strict filter is empty
  if (matched.length === 0) {
    matched = LIFE_EVENTS_POOL.filter((e) => e.stageKey === currentStage.key && !answeredTitles.has(e.titleVi));
  }
  if (matched.length === 0) {
    matched = LIFE_EVENTS_POOL.filter((e) => e.stageKey === currentStage.key);
  }

  return matched;
}

export function applyLifeChoice(
  state: OneLifeState,
  event: LifeEvent,
  choice: LifeChoice
): { updatedState: OneLifeState; isDeath: boolean; causeOfDeath?: string } {
  const newAttributes: CharacterAttributes = {
    health: Math.min(100, Math.max(0, state.attributes.health + choice.healthDelta)),
    happiness: Math.min(100, Math.max(0, state.attributes.happiness + choice.happinessDelta)),
    wealth: Math.min(100, Math.max(0, state.attributes.wealth + choice.wealthDelta)),
    intellect: Math.min(100, Math.max(0, state.attributes.intellect + choice.intellectDelta)),
    relations: Math.min(100, Math.max(0, state.attributes.relations + choice.relationsDelta)),
  };

  const decisionRecord: LifeDecisionRecord = {
    age: event.age,
    stageKey: event.stageKey,
    eventTitle: event.titleVi,
    choiceText: choice.textVi,
    outcomeText: choice.outcomeTextVi,
    consequences: {
      healthDelta: choice.healthDelta,
      happinessDelta: choice.happinessDelta,
      wealthDelta: choice.wealthDelta,
      intellectDelta: choice.intellectDelta,
      relationsDelta: choice.relationsDelta,
    },
  };

  const newAchievements = [...state.achievements];
  if (choice.unlockAchievement && !newAchievements.includes(choice.unlockAchievement)) {
    newAchievements.push(choice.unlockAchievement);
  }

  let newCareer = state.career;
  if (choice.careerTag) {
    newCareer = choice.careerTag;
  }

  let newEducation = choice.unlockEducation || state.educationVi;
  let newRelationship = choice.unlockRelationship || state.relationshipStatus;
  let newChildrenCount = state.childrenCount + (choice.addChildren || 0);

  const newPlacesLived = [...state.placesLived];
  if (choice.unlockPlacesLived && !newPlacesLived.includes(choice.unlockPlacesLived)) {
    newPlacesLived.push(choice.unlockPlacesLived);
  }

  // Update Catholic Faith Sacraments and Virtues
  let newFaithLife = state.faithLife ? { ...state.faithLife } : undefined;
  if (newFaithLife) {
    if (choice.addSacrament && !newFaithLife.sacramentsReceived.includes(choice.addSacrament)) {
      newFaithLife.sacramentsReceived = [...newFaithLife.sacramentsReceived, choice.addSacrament];
    }
    if (choice.addVirtue && !newFaithLife.charityAndVirtues.includes(choice.addVirtue)) {
      newFaithLife.charityAndVirtues = [choice.addVirtue, ...newFaithLife.charityAndVirtues];
    }
  }

  // Check death probability
  const currentStage = LIFE_STAGES[state.currentStageIndex];
  const targetAge = event.age || currentStage.maxAge;
  
  // Natural mortality based on age, health, and national average life expectancy
  const nationalAvg = state.gender === 'male' ? state.country.lifeExpectancyMale : state.country.lifeExpectancyFemale;
  let isDeath = false;
  let causeOfDeath: string | undefined;

  if (choice.isFatal) {
    isDeath = true;
    causeOfDeath = choice.causeOfDeathVi || 'Tạ thế bình yên, khép lại kiếp nhân sinh';
  } else if (newAttributes.health <= 5) {
    isDeath = true;
    causeOfDeath = 'Suy kiệt sức khỏe nghiêm trọng';
  } else if (choice.mortalityRiskBonus && Math.random() < choice.mortalityRiskBonus) {
    isDeath = true;
    causeOfDeath = 'Biến cố tai nạn rủi ro';
  } else if (targetAge > 70) {
    const oldAgeFactor = Math.max(0, (targetAge - nationalAvg) * 0.08);
    const healthProtection = (newAttributes.health / 100) * 0.15;
    const deathChance = Math.max(0.05, oldAgeFactor - healthProtection);

    if (targetAge >= 90 || Math.random() < deathChance) {
      isDeath = true;
      causeOfDeath = 'Tuổi cao sức yếu, tạ thế bình yên trong giấc ngủ';
    }
  }

  // Stage progression logic: Fast mode (1 event/stage) vs Deep mode (2 events/stage)
  const currentStageEventsCompleted = (state.decisionHistory.filter((d) => d.stageKey === currentStage.key).length) + 1;
  const maxEventsInStage = state.isFastMode ? 1 : 2;
  const shouldAdvanceStage = currentStageEventsCompleted >= maxEventsInStage;

  const nextStageIndex = shouldAdvanceStage ? state.currentStageIndex + 1 : state.currentStageIndex;
  const isFinishedStages = nextStageIndex >= LIFE_STAGES.length;

  const resolvedCause = causeOfDeath || (isFinishedStages ? 'Đại thọ viên mãn tạ thế' : state.causeOfDeathVi);

  const updatedState: OneLifeState = {
    ...state,
    currentAge: targetAge,
    currentStageIndex: isDeath || isFinishedStages ? state.currentStageIndex : nextStageIndex,
    attributes: newAttributes,
    career: newCareer,
    educationVi: newEducation,
    relationshipStatus: newRelationship,
    childrenCount: newChildrenCount,
    placesLived: newPlacesLived,
    faithLife: newFaithLife,
    achievements: newAchievements,
    decisionHistory: [...state.decisionHistory, decisionRecord],
    isAlive: !isDeath && !isFinishedStages,
    completedAt: isDeath || isFinishedStages ? Date.now() : undefined,
    causeOfDeathVi: resolvedCause,
  };

  return {
    updatedState,
    isDeath: isDeath || isFinishedStages,
    causeOfDeath: resolvedCause,
  };
}

export function compileLifeSummary(state: OneLifeState): LifeSummary {
  const nationalAvg = state.gender === 'male'
    ? state.country.lifeExpectancyMale
    : state.country.lifeExpectancyFemale;

  const comparison = Number((state.currentAge - nationalAvg).toFixed(1));

  // Approximate percentile ranking based on normal distribution around national average
  // e0 standard deviation in human populations is typically around 12-14 years
  const zScore = (state.currentAge - nationalAvg) / 13;
  let percentile = Math.round(50 + 40 * Math.tanh(zScore * 0.8));
  percentile = Math.max(1, Math.min(99, percentile));

  // Education computation
  let educationVi = state.educationVi;
  if (!educationVi || educationVi === 'Giáo dục Khởi đầu') {
    if (state.attributes.intellect >= 75) {
      educationVi = 'Thạc sĩ / Học giả Chuyên sâu';
    } else if (state.attributes.intellect >= 55) {
      educationVi = 'Cử nhân Đại học Chính quy';
    } else if (state.attributes.intellect >= 35) {
      educationVi = 'Cao đẳng Kỹ thuật & Thực hành Nghề';
    } else if (state.attributes.intellect < 20) {
      educationVi = 'Giáo dục Cơ sở & Tự Lập Thực Tế';
    } else {
      educationVi = 'Tốt nghiệp Trung học Phổ thông';
    }
  }

  // Cause of death computation
  let causeOfDeathVi = state.causeOfDeathVi;
  if (!causeOfDeathVi) {
    if (state.currentAge >= 85) {
      causeOfDeathVi = 'Đại thọ viên mãn, an nghỉ thanh thản trong giấc ngủ';
    } else if (state.currentAge >= 70) {
      causeOfDeathVi = 'Tuổi cao sức yếu, tạ thế bình yên nơi quê nhà';
    } else if (state.attributes.health <= 15) {
      causeOfDeathVi = 'Suy kiệt sức khỏe sau thời gian dài điều trị';
    } else {
      causeOfDeathVi = 'Biến cố rủi ro bất ngờ trong cuộc đời';
    }
  }

  // Catholic Faith Life computation
  const saintsPool = state.gender === 'male' ? CATHOLIC_SAINTS_MALE : CATHOLIC_SAINTS_FEMALE;
  const saintName = state.saintName || saintsPool[0].name;
  const saintObj = saintsPool.find((s) => s.name === saintName) || saintsPool[0];
  const faithLife = state.faithLife || computeFaithLife(state.gender, state.currentAge, state.relationshipStatus, saintName, saintObj.patron);

  return {
    id: state.id,
    characterName: state.characterName,
    saintName,
    gender: state.gender,
    birthYear: state.birthYear,
    deathAge: state.currentAge,
    isAlive: false,
    country: state.country,
    regionNameVi: state.regionNameVi,
    familyClassVi: state.familyClassVi,
    educationVi,
    causeOfDeathVi,
    faithLife,
    finalCareer: state.career,
    finalRelationshipStatus: state.relationshipStatus,
    childrenCount: state.childrenCount,
    finalAttributes: state.attributes,
    majorAchievements: state.achievements,
    keyDecisions: state.decisionHistory,
    placesLived: state.placesLived,
    nationalLifeExpectancy: nationalAvg,
    comparisonWithNationalAvg: comparison,
    percentileRank: percentile,
    shareCode: `LIFE_${state.country.code}_${state.currentAge}_${state.id.slice(-4).toUpperCase()}`,
    completedAt: state.completedAt || Date.now(),
  };
}
